// Angular
import { Injectable, OnDestroy } from '@angular/core';
import { NgModel } from '@angular/forms';
import { Subscription } from 'rxjs';

// SyncFusion
import { DataManager, Query } from '@syncfusion/ej2-data';
import { GridComponent, ColumnModel, QueryCellInfoEventArgs, DropDownEditCell, BooleanEditCell, AggregateColumnModel, AggregateRowModel, Column, DefaultEditCell, DatePickerEditCell, NumericEditCell, ICellFormatter, parentsUntil, BatchEdit, Cell } from '@syncfusion/ej2-angular-grids';

// Third party
import { CerGridCommandService } from './cer-grid-command.service';
import { FieldFormatType, FieldMetaForeignKey, FieldMetadata, FieldMetadataUtil, FieldWidthEnum, UiCommandRenderCell } from '@data/cer-data-types';
import { CerDataMetaService } from '@data/cer-data-meta.service';
import { CerGridEditTextArea } from './cer-grid-edit-textarea';
import { CerGridEditDropDownList } from './cer-grid-edit-dropdownlist';
import { CerGridEditBoolean } from './cer-grid-edit-boolean';
import { CerGridSelectionService } from './cer-grid-selection.service';
import { CerGridEditDefault } from './cer-grid-edit-default';
import { CerGridEditDatePicker } from './cer-grid-edit-date-picker';
import { CerLocaleService } from '@ui/cer-ui-locale.service';
import { CerGridEditNumeric } from './cer-grid-edit-numeric';
import { CerDataService } from '@data/cer-data.service';
import { CerEventSubAction, UiActionTypeEnum, UiCommandEvent, UiCommandSourceEnum } from '@ui/cer-ui-types';
import { CerGridEditDropDownMultiList } from './cer-grid-edit-dropdownmultilist';
import { CerGridEditColorPicker } from './cer-grid-edit-color-picker';
import { isNullOrUndefined } from '@syncfusion/ej2-base';

@Injectable()
export class CerGridEditCellService implements OnDestroy {

  // Subscriptions
  private subscriptionManager: Subscription = new Subscription();

  // Grid edit
  private grid: GridComponent;
  private gridAggregates: AggregateRowModel[] = [];

  public init(grid: GridComponent) {
    this.grid = grid;
    this.footerInit()
  }

  public initPost() {
    this.queryFieldInfoFieldsInit();
    this.onCellSelectedFieldNamesInit();
    this.keyActionHandlerInit();
  }
  
  constructor(
    private command: CerGridCommandService,
    private data: CerDataService,
    private selection: CerGridSelectionService,
    private meta: CerDataMetaService,
    private localeService: CerLocaleService
  ) {
    this.subscriptionManager.add(this.command.commandPre$.subscribe(event => this.onCommandPre(event)));
  }

  // Subscriptions
  ngOnDestroy(): void {
    this.subscriptionManager.unsubscribe();
    if (this.chatColumn) {
      this.grid.element.removeEventListener("click", (e) => this.onChatClicked(e));
    }
  }

  private onCommandPre(event: UiCommandEvent) {
    if (event.cancel) {
      return;
    }
    switch (event.source) {
      case UiCommandSourceEnum.ActionBegin:
        switch (event.actionType) {
          case UiActionTypeEnum.QueryCellInfo:
            this.onQueryCellInfoActionBegin(event.args);
            break;
        }
        break;
      case UiCommandSourceEnum.ActionComplete:
        switch (event.actionType) {
          case UiActionTypeEnum.Created:
            this.chatColumnInit();
            this.editCellCerInit(event.subAction);
            break;
          case UiActionTypeEnum.CellSelect:
            if (this.onCellSelectedFieldNames?.length > 0 && event.columnName) {
              if (this.onCellSelectedFieldNames.includes(event.columnName)) {
                const fieldSub = FieldMetadataUtil.fieldByCommandEvent(event)?.onCellSelected;
                if (fieldSub) {
                  fieldSub(event);
                }
              }
            }
            break;
        }
        break;
    }
  }

  public foreignKeySetup(column: ColumnModel, meta: FieldMetadata) {
    column.foreignKeyField = FieldMetaForeignKey.keyFieldGet(meta);
    column.foreignKeyValue = FieldMetaForeignKey.valueFieldGet(meta);
    column.editType = "dropdownedit";
    this.columnSetupDDL(column, meta);
    if (column.foreignKeyField == column.foreignKeyValue && column.foreignKeyValue?.length > 0) {
      if (meta.formatter == null) {
        column.formatter = (column: Column, data: Object) => { return this.columnFKSameKeyValueFormatterGetValue(column, data); };
      }
    }
  }

  //export class ColumnFKSameKeyValueFormatter implements ICellFormatter {
  public columnFKSameKeyValueFormatterGetValue(column: Column, data: Object): Object {
    var rowData: any = data;
    if (!rowData || column?.field == null) {
      return '';
    }
    return rowData[column.field];
  }

  private columnSetupDDL(column: ColumnModel, meta: FieldMetadata) {
    if (meta.foreignKeyTableName) {
      var dm: DataManager = this.data.fkDataManagerSetup(meta);
      column.dataSource = dm;
    }
    else {
      if (meta.foreignKey?.dataOptions?.subject) {
        column.dataSource = new DataManager(meta.foreignKey.dataOptions.subject.getValue());
        this.subscriptionManager.add(meta.foreignKey?.dataOptions?.subject.subscribe(data => {
          column.dataSource = new DataManager(data);
          //this.columnSetupDDLParams(column, meta, dm);
          this.data.onFKTableLoaded();
        }));
      }
    }
  }

  private keyActionHandlerInit() {
    (<any>this.grid).keyActionHandler = CerGridEditCellService.keyActionHandler;
  }

  private static keyActionHandler(e: any) {
    const grid: GridComponent = e.currentTarget?.ej2_instances[0];
    const gridAny: any = grid;
    if (gridAny.isChildGrid(e) ||
      (gridAny.isEdit && e.action !== 'escape' && e.action !== 'enter' && e.action !== 'shiftEnter'
        && e.action !== 'tab' && e.action !== 'shiftTab')) {
      if (grid.editSettings.mode == 'Batch' && (e.action == 'downArrow' || e.action === 'upArrow')) {
        const editCellCurrent = CerGridEditCellService.editCellCurrent(grid, e.target);
        grid.editModule.saveCell();
        if (editCellCurrent?.index != null) {
          CerGridEditCellService.editCellNextPrev(grid, editCellCurrent, e.action == 'downArrow');
        }
        gridAny.keyPress = true;
      }
      else {
        return;
      }
    }
    else {
      gridAny.keyPress = true;
    }
    if (gridAny.allowKeyboard) {
      if (e.action === 'ctrlPlusP') {
        e.preventDefault();
        gridAny.print();
      }
      gridAny.notify('key-pressed', e);
    }
  }
  static editCellNextPrev(grid: GridComponent, cell: Cell<Column>, next: boolean) {

    const rowIndex = cell.index + (next ? 1 : -1);
    if (cell?.column?.field == null ||
      rowIndex < 0 ||
      rowIndex >= grid.getDataRows().length) {
      return;
    }
    //const isAddRow : boolean = (<any> grid.editModule).isAddRow(rowIndex);
    grid.editModule.editCell(rowIndex, cell.column.field /*, isAddRow*/);
  }
  static editCellCurrent(grid: GridComponent, target: Element): Cell<Column> {
    var target = parentsUntil(target, 'e-rowcell');
    var tr = parentsUntil(target, 'e-row');
    var rowIndex = tr && parseInt(tr.getAttribute('data-rowindex'), 10);
    var colIndex = target && parseInt(target.getAttribute('data-colindex'), 10);
    if (!isNullOrUndefined(target) && !isNullOrUndefined(rowIndex) && !isNaN(colIndex)
      && target.parentElement.classList.contains('e-editedrow')) {
      const col = grid.getColumns()[colIndex];
      if (col?.allowEditing === true) {
        return new Cell<Column>({ column: col, index: rowIndex, colIndex: colIndex });
      }
    }
    return null;
  }


  // Purpose: Wrap standard edit cell with our edit cell
  // We cannot replace the base/standard edit cell with our own edit cell, so we have to wrap the base/standard edit cell with our own edit cell
  // We save our own edit cell in the column object as a property "cerEdit"
  private editCellCerInit(action: CerEventSubAction) {
    if (this.grid?.columns) {
      this.grid.columns.forEach((column: ColumnModel) => {
        const fieldsMeta = this.meta.fieldMetadata;
        const fieldMeta = this.meta.columnMetaGet(column);

        var editType = column.editType;
        if (column.editType == 'dropdownedit') {
          if (fieldMeta != null && (fieldMeta.allowEdit === false && fieldMeta.allowEditOnAdd != true)) {
            editType = 'default';
          }
        }
        switch (editType) {
          case 'dropdownedit':
            var editCellStdDdl: DropDownEditCell = <DropDownEditCell>column.edit;
            if (fieldMeta.format == 'Chips') {
              //var editCellStdDdl: DropDownEditCell = <DropDownEditCell;
              var fkQuery: Query = this.data.fkQuery(fieldMeta);
              (<any>column).cerEdit = new CerGridEditDropDownMultiList(this.grid, editCellStdDdl, fieldMeta, fieldsMeta, fkQuery);
              //(<any> column.edit.params).ddEditedData = true; // Avoid SF DDL changes to col. DS data

            }
            else {
              var fkQuery: Query = this.data.fkQuery(fieldMeta);
              (<any>column).cerEdit = new CerGridEditDropDownList(this.grid, editCellStdDdl, fieldMeta, fieldsMeta, fkQuery);
              (<any>column.edit.params).ddEditedData = true; // Avoid SF DDL changes to col. DS data
            }
            break;
          case 'datepickeredit':
            var editCellStdDatePicker: DatePickerEditCell = <DatePickerEditCell>column.edit;
            (<any>column).cerEdit = new CerGridEditDatePicker(this.grid, editCellStdDatePicker, fieldMeta, fieldsMeta, this.localeService);
            break;
          case 'booleanedit':
            var editCellStdBoolean: BooleanEditCell = <BooleanEditCell>column.edit;
            (<any>column).cerEdit = new CerGridEditBoolean(this.grid, editCellStdBoolean, fieldMeta, fieldsMeta);
            break;
          case 'numericedit':
            var editCellStdNumeric: NumericEditCell = <NumericEditCell>column.edit;
            (<any>column).cerEdit = new CerGridEditNumeric(this.grid, editCellStdNumeric, fieldMeta, fieldsMeta, this.localeService);
            break;
          case 'coloredit':
            var editCellColorPicker: NumericEditCell = <NumericEditCell>column.edit;
            (<any>column).cerEdit = new CerGridEditColorPicker(this.grid, editCellColorPicker, fieldMeta, fieldsMeta);
            break;
          case undefined:
            var editCellDefault: DefaultEditCell = <DefaultEditCell>column.edit;
            (<any>column).cerEdit = new CerGridEditDefault(this.grid, editCellDefault, fieldMeta, fieldsMeta);
            break;
        }
      });
    }
  }

  // Format
  public columnSetFormat(column: ColumnModel, meta: FieldMetadata) {

    if (meta.width !== undefined) {
      column.width = meta.width;
    }
    else if (meta.widthType != undefined) {
      column.width = this.columnWidthByType(meta.widthType);
    }
    if (meta.minWidth !== undefined) {
      column.minWidth = meta.minWidth;
    }
    if (meta.maxWidth !== undefined) {
      column.maxWidth = meta.maxWidth;
    }
    else if (meta.maxWidthType != undefined) {
      column.maxWidth = this.columnWidthByType(meta.maxWidthType);
    }

    if (meta.cellAttributes) {
      column.customAttributes = meta.cellAttributes;
    }

    if (meta.formatter) {
      column.formatter = meta.formatter;
      column.disableHtmlEncode = false; // for html formatted text
    }

    if (meta.template) {
      column.template = meta.template;
    }

    if (!meta.format && meta.name == 'id') {
      meta.format = 'Integer';
    }
    if (meta.format) {
      if (Array.isArray(meta.format)) {
        meta.format.forEach(formatEle => this.columnSetFormatElement(column, meta, formatEle));
      }
      else {
        this.columnSetFormatElement(column, meta, meta.format);
      }
    }
    this.columnSetFormatDefault(column);
    this.columnSetWidthDefault(column);
  }

  private columnSetFormatDefault(column: ColumnModel) {
    if (column.type === undefined) {
      column.type = "string";
    }
    // Default format
    if (!column.format) {
      switch (column.type) {
        case "number":
          column.format = "N0";
          column.textAlign = "Right";
          break;
        case "date":
          column.format = { type: "date", format: "dd.MM.yyyy" };
          break;
      }
    }
  }

  public columnSetupByMeta(column: ColumnModel, meta: FieldMetadata) {
    if (column == null) {
      return;
    }

    (<any>column).meta = meta;

    // Header
    column.headerText = meta.text;
    column.visible = (meta.visible !== undefined) ? meta.visible : true;

    if (meta.headerTemplate !== undefined) {
      column.headerTemplate = meta.headerTemplate;
    }

    // Data
    if (meta.isIdentity != undefined) {
      column.isIdentity = meta.isIdentity;
    }

    // Foreign Key Setup
    if (meta.foreignKey?.dataOptions?.subject || meta.foreignKeyTableName) {
      this.foreignKeySetup(column, meta);
    }

    if (meta.allowEdit !== undefined) {
      column.allowEditing = meta.allowEdit;
    }

    if (meta.defaultValue !== undefined) {
      column.defaultValue = meta.defaultValue;
    }

    var validationRules: any = null;
    if (meta.required !== undefined && meta.required) {
      if (validationRules == null) {
        validationRules = new Object();
      }
      validationRules.required = [true, '"' + meta.text + '" skal indtastes'];
    }
    if (meta.validationMaxLen !== undefined) {
      if (validationRules == null) {
        validationRules = new Object();
      }
      validationRules.maxLength = meta.validationMaxLen;
    }
    column.validationRules = validationRules;

    // Format
    this.columnSetFormat(column, meta);

    // Footer
    if (!(meta.footerType == null && meta.footerTemplate == null && meta.footerFunction == null)) {
      this.footerAddColumn(column, meta);
    }
  }

  public columnWidthByType(widthType: FieldWidthEnum): string {
    return (<number>widthType) + 'px';
  }

  private columnSetWidthDefault(column: ColumnModel) {
    // Default width
    if (!column.width) {
      switch (column.type) {
        case "number":
          column.width = this.columnWidthByType(FieldWidthEnum.XS);
          break;
        case "date":
          column.width = this.columnWidthByType(FieldWidthEnum.S);
          break;
        case "string":
          column.width = this.columnWidthByType(FieldWidthEnum.M);
          break;
      }
    }
  }

  public columnSetFormatElement(column: ColumnModel, meta: FieldMetadata, format: FieldFormatType) {
    switch (format) {
      case 'Amount':
      case 'Amount0':
        this.columnSetFormatElementAsAmount(column, format == 'Amount');
        break;
      case 'AmountPct':
      case 'AmountPct0':
        this.columnSetFormatElementAsAmount(column, format == 'AmountPct', '%');
        break;
      case 'NumberLeft':
        column.type = "number";
        column.editType = "numericEdit";
        column.edit = { params: { validateDecimalOnType: true, decimals: 0, format: "N" } };
        column.format = "N";
        column.textAlign = 'Left';
        if (!column.width) {
          column.width = this.columnWidthByType(FieldWidthEnum.XS);
        }
        break;
      case 'Number':
      case 'Number0':
        column.type = "number";
        column.editType = "numericEdit";
        column.edit = { params: { validateDecimalOnType: true, decimals: 0, format: "N" } };
        column.format = "N";
        column.textAlign = "Right";
        if (!column.width) {
          column.width = this.columnWidthByType(FieldWidthEnum.XS);
        }
        if (format == "Number0") {
          column.formatter = (column: Column, data: any) => { var s = data[column.field]; return (s == null || s == 0 ? '' : s); };
        }
        break;
      case 'Integer':
        column.type = "number";
        column.editType = "numericEdit";
        column.edit = { params: { validateDecimalOnType: true, decimals: 0, format: "#" } };
        column.format = "#";
        column.textAlign = "Right";
        if (!column.width) {
          column.width = this.columnWidthByType(FieldWidthEnum.XS);
        }
        break;
      case 'IntegerPct':
        column.type = "number";
        column.editType = "numericEdit";
        column.edit = { params: { validateDecimalOnType: true, decimals: 0, format: "N" } };
        column.format = "N";
        column.formatter = (column: Column, data: any) => { var s = data[column.field]; return (s == null ? '' : s + '%'); };
        column.textAlign = "Right";
        if (!column.width) {
          column.width = this.columnWidthByType(FieldWidthEnum.XS);
        }
        break;
      case 'IntegerColor':
        column.type = "number";
        column.editType = "colorEdit";
        column.edit = { params: { validateDecimalOnType: true, decimals: 0, format: "N" } };
        if (meta.onRenderCell == null) {
          meta.onRenderCell = (e => { CerGridEditCellService.onColorRenderCell(e, e.data != null ? e.data[column.field] : null) });
          this.queryFieldInfoFieldsInit();
        }
        //column.format = "N";
        column.textAlign = "Right";
        if (!column.width) {
          column.width = this.columnWidthByType(FieldWidthEnum.XS);
        }
        break;
      case 'DateLong':
        column.type = "datetime";
        column.editType = "datetimepickerEdit";
        column.format = { type: "date", format: "dd.MM.yyyy HH:mm" };
        if (!column.width) {
          column.width = this.columnWidthByType(FieldWidthEnum.M);
        }
        break;
      case 'DateShort':
        column.type = "date";
        column.editType = "datePickerEdit";
        column.format = { type: "date", format: "dd.MM.yyyy" };
        if (!column.width) {
          column.width = this.columnWidthByType(FieldWidthEnum.S);
        }
        break;
      case 'DateMonthYear':
        column.type = "date";
        column.editType = "datePickerEdit";
        column.format = { type: "date", format: "MMM yy" };
        if (!column.width) {
          column.width = this.columnWidthByType(FieldWidthEnum.S);
        }
        break;
      case 'DateYear':
        column.type = "date";
        column.editType = "datePickerEdit";
        column.format = { type: "date", format: "yyyy" };
        if (!column.width) {
          column.width = this.columnWidthByType(FieldWidthEnum.XS);
        }
        break;
      case 'DateTime':
        column.type = "datetime";
        column.editType = "dateTimePickerEdit";
        column.format = { type: "date", format: "dd.MM.yyyy HH:mm" };
        if (!column.width) {
          column.width = this.columnWidthByType(FieldWidthEnum.S);
        }
        break;
      case 'CheckBox':
        column.type = "boolean";
        column.displayAsCheckBox = true;
        column.editType = "booleanedit";
        if (!column.width) {
          column.width = this.columnWidthByType(FieldWidthEnum.XS);
          //column.maxWidth = this.columnWidthByType(FieldWidthEnum.S);
        }
        break;
      case 'CheckBoxInt':
        column.type = "boolean";
        column.displayAsCheckBox = true;
        column.editType = "booleanedit";
        if (!column.width) {
          column.width = this.columnWidthByType(FieldWidthEnum.XS);
          //column.maxWidth = this.columnWidthByType(FieldWidthEnum.S);
        }
        break;
      case 'TextRight':
        column.type = "string";
        column.textAlign = 'Right';
        break;
      case 'TextCenter':
        column.type = "string";
        column.textAlign = 'Center';
        break;
      case 'Text':
        column.type = "string";
        break;
      case 'TextArea':
        column.type = "string";
        column.edit = new CerGridEditTextArea(this.grid, column);
        break;
      case 'Url':
        column.type = "string";
        column.template = this.templateUrl(column.field, meta?.urlBase);
        if (meta?.urlBase == null) {
          var validationRuleUrl: object = { url: [true, 'En valid web adresse skal angives i feltet "' + meta.text + '".'] };
          column.validationRules = validationRuleUrl;
        }
        break;
      case 'UrlBase':
        column.type = "string";
        column.template = this.templateUrl(column.field, meta?.urlBase);
        break;
      case 'Email':
        column.type = "string";
        column.template = this.templateEmail(column.field);
        if (!column.width) {
          column.width = this.columnWidthByType(FieldWidthEnum.L);
        }
        var validationRuleEmail: object = { email: [true, 'En valid e-mail skal angives i feltet "' + meta.text + '".'] };
        column.validationRules = validationRuleEmail;

        break;
      case 'Phone':
        column.type = "string";
        column.template = this.templatePhone(column.field);
        break;
      case 'Chip':
        column.template = this.templateChip(column.field, meta.formatClass ?? 'chip-primary');
        break;
      case 'Chips':
        column.template = this.templateChips(column.field, meta.formatClass ?? 'chip-primary');
        break;
      case 'Chat':
        column.allowEditing = false;
        column.allowFiltering = false;
        column.allowGrouping = false;
        column.allowSearching = false;
        column.allowSorting = false;
        this.chatColumn = column;
        if (this.chatTemplate) {
          (<any>column.template) = this.chatTemplate;
        }
        //column.template = '<ng-template #template let-data><input [value]="data.id" />><mat-icon color="primary" class="icon-small" [matBadgeHidden] = "!data.chat" [matBadge]="data.chat" matBadgeColor="info">chat</mat-icon></ng-template>';
        //column.template = "\${ if (chat === undefined)}<mat-icon color='primary' class='icon-small' matBadgeHidden=true [matBadge]=" + column.field.length + " matBadgeColor='info'>chat</mat-icon>\${/if}";
        //column.template = "\${ if (" + column.field + ")}chat</mat-icon>";
        if (!column.width) {
          column.width = this.columnWidthByType(FieldWidthEnum.XXXS);
        }
        break;
    }
  }

  private columnSetFormatElementAsAmount(column: ColumnModel, showZero: boolean = true, suffix: string = null) {
    column.type = "number";
    column.editType = "numericEdit";
    column.edit = { params: { validateDecimalOnType: true, decimals: 2, format: "N2" } };
    column.format = "N2";
    column.textAlign = "Right";
    if (!column.width) {
      column.width = this.columnWidthByType(FieldWidthEnum.S);
    }
    if (showZero == false || suffix?.length > 0) {
      suffix ??= "";
      column.formatter = (column: Column, data: any) => { var s = data[column.field]; return (s == null || (s == 0 && !showZero) ? '' : column.getFormatter()(s) + suffix); };
    }
  }

  /*************
   * Footer
   *************/
  private footerInit() {
    this.gridAggregates = [];
  }

  private footerAddColumn(column: ColumnModel, meta: FieldMetadata) {
    var aggregate: AggregateColumnModel = {
      field: column.field,
      type: meta.footerType,
      customAggregate: meta.footerFunction,
      format: column.format
    };

    var aggregateRow: AggregateRowModel;
    if (this.gridAggregates.length == 0) {
      aggregateRow = { columns: [] };
      this.gridAggregates.push(aggregateRow);
    }
    else {
      aggregateRow = this.gridAggregates[0];
    }
    aggregateRow.columns.push(aggregate);
    this.grid.aggregates = this.gridAggregates;
  }


  /**********************
   * Template field type
   **********************/
  private templateUrl(field: string, urlPrefix: string): string {
    var fieldPrefix: string = urlPrefix ?? 'https://';
    return "<span>\${if(" + field + ")}<a rel='nofollow' class='cer-link' target='_blank' href='" + fieldPrefix + "${" + field + "}'>${" + field + "}</a>\${/if}";
  }

  private templateEmail(field: string): string {
    return "<div>\${if(" + field + ")}<a rel='nofollow' class='cer-link' href = 'mailto:\${" + field + "}' >\${ " + field + " }</a>\${/if}</div>";
  }

  private templatePhone(field: string): string {
    return "<div>\${ if (" + field + ")}<a rel='nofollow' class='cer-link' href='tel:\${" + field + "}'>\${" + field + "}</a>\${/if}</div>"
  }

  private templateChip(field: string, cls: string): string {
    return "<div>\${ if (" + field + ")}<div class='e-chip-list'><div class='e-chip " + cls + "'><div class='e-chip-text'>\${" + field + "}</div></div></div>\${/if}</div>";
  }

  private templateChips(field: string, cls: string): string {
    return "<div>\${ if (" + field + ")}<div class='e-chip-list'>\${for(item of " + field + ")}<div class='e-chip " + cls + "'><div class='e-chip-text'>\${item}</div></div>\${/for}</div>\${/if}</div>";
  }

  /******************************
  * QueryFieldInfo
  ******************************/
  public onCellSelectedFieldNames: string[] = [];
  public queryCellInfoFieldNames: string[] = [];
  public onRenderCellFieldNames: string[] = [];

  private queryFieldInfoFieldsInit() {
    this.queryCellInfoFieldNames = this.data?.fieldMetadata?.filter(f => f.onQueryCellInfo).map(f => f.name);
    if (this.queryCellInfoFieldNames.length > 0) {
      this.command.onRenderCellEventEnabled = true;
    }
    this.onRenderCellFieldNames = this.data?.fieldMetadata?.filter(f => f.onRenderCell != null).map(f => f.name);
    if (this.onRenderCellFieldNames.length > 0) {
      this.command.onRenderCellEventEnabled = true;
    }
    this.command.onExcelCellFieldNames = this.data?.fieldMetadata?.filter(f => f.onExcelCell).map(f => f.name);
    if (this.command.onExcelCellFieldNames.length > 0) {
      this.command.onExcelCellEventEnabled = true;
    }
  }

  private onQueryCellInfoActionBegin(args: QueryCellInfoEventArgs) {
    if (args.column?.field?.length > 0) {
      const columnName = args.column.field;
      if (this.onRenderCellFieldNames.includes(columnName)) {
        const meta: FieldMetadata = FieldMetadataUtil.fieldMetaByColumn(args.column);
        if (meta.onRenderCell != null) {
          var e: UiCommandRenderCell = {
            //args: args,
            data: args.data,
            column: args.column,
            columnName: columnName,
            fieldMeta: meta,
            foreignKeyData: args.foreignKeyData as boolean,
            cell: args.cell,
            value: (args as any).value ?? FieldMetadataUtil.valueGet<number>(args.data, meta),
            style: (args.cell as HTMLElement).style,
            colSpan: args.colSpan,
            rowSpan: args.rowSpan,
            isExcel: false
          };
          meta.onRenderCell(e);
        };
      }
      else if (this.queryCellInfoFieldNames.includes(columnName)) {
        // Legacy
        this.data?.fieldMetadata.filter(f => f.name == columnName && f.onQueryCellInfo != null)
          .forEach(f => f.onQueryCellInfo(args));
      }
      this.chatCellInfo(args);
    }
  }

  /******************************
  * onCellSelected
  ******************************/

  private onCellSelectedFieldNamesInit() {
    this.onCellSelectedFieldNames = this.data?.fieldMetadata?.filter(f => f.onCellSelected).map(f => f.name);
  }

  /******
  * Color
  *******/
  private static onColorRenderCell(e: UiCommandRenderCell, color: number): void {
    if (color) {
      const ele = (e.cell as HTMLElement);
      const row = e.data as any;
      if (ele) {
        ele.innerHTML = '';
        ele.style.backgroundColor = "#" + row.color.toString(16);
      }
    }
  }

  /******
  * Chat
  *******/
  public chatTemplate: NgModel = null;
  public chatColumn: ColumnModel = null;

  private chatColumnInit() {
    if (this.chatColumn) {
      this.grid.element.addEventListener("click", this.onChatClicked.bind(this));
      this.command.onRenderCellEventEnabled = true;
    }
  }

  private onChatClicked(args: any) {
    if ((args.target as any).classList.contains("chat-link-cell")) {
      this.command.cmdDispatchCommand(UiActionTypeEnum.ChatClicked, args);
    }
  }

  public chatColumnRefresh(data: object) {
    var idx = this.selection.rowSelectedRowIdx();
    if (idx != undefined && idx !== null && data && this.chatColumn && this.chatColumn.field) {
      try {
        var column: Column = this.grid.getColumnByField(this.chatColumn.field);
        var cell: Element = this.grid.getCellFromIndex(idx, column.index);
        var args: QueryCellInfoEventArgs = { column: column, cell: cell, data: data };
        this.chatCellInfo(args);
      }
      catch {
        console.log("Chat refresh failed");
      }
    }
  }

  private chatCellInfo(args: QueryCellInfoEventArgs) {

    if (this.chatColumn?.field && args.column?.field === this.chatColumn?.field) {
      args.cell.classList.add('chat-link-cell');

      args.cell.classList.remove('chat-link-active');
      args.cell.classList.remove('chat-link-none');
      args.cell.classList.remove('chat-link-unknown');
      var data: any = args.data;
      var chat = data[this.chatColumn.field];
      //console.log(data.id);
      //console.log(chat);
      if (chat) {
        if (chat.length > 0) {
          args.cell.classList.add('chat-link-active');
        }
        else {
          args.cell.classList.add('chat-link-none');
        }
      }
      else {
        args.cell.classList.add('chat-link-unknown');
      }
    }
  }
}
